import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface EmptyStateProps {
  /** Decorative icon rendered above the title (aria-hidden). */
  icon?: ReactNode;
  title: string;
  description?: string;
  /** Optional follow-up control; policy-gated by the caller (never a dead link). */
  action?: ReactNode;
  className?: string;
}

/**
 * Mirrors `apps/admin-ui/src/components/shared/EmptyState.tsx` (same props API).
 * Serves the design's empty/error views (§16.2/§16.3): explain the situation and
 * offer the next action only when one exists.
 */
export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-lg border border-dashed bg-card px-6 py-12 text-center',
        className,
      )}
    >
      {icon ? (
        <div className="mb-4 text-muted-foreground [&>svg]:h-8 [&>svg]:w-8" aria-hidden="true">
          {icon}
        </div>
      ) : null}
      <h3 className="text-lg font-semibold text-card-foreground">{title}</h3>
      {description ? (
        <p className="mt-2 max-w-md text-sm text-muted-foreground">{description}</p>
      ) : null}
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  );
}
